/**
 * /v1 parish members contract (Phase 6).
 * additionalInfo holds `custom` (e.g. parishionerId), merged key by key on PATCH.
 */

export type MemberAdditionalInfo = {
    custom?: Record<string, unknown>;
    /** Legacy-shaped key some rows may still carry at the root. */
    parishionerId?: string | null;
    [key: string]: unknown;
};

export type ApiMember = {
    id: string;
    isDefault: boolean;
    joinedAt: string;
    additionalInfo: MemberAdditionalInfo | null;
    user: {
        id: string;
        email: string;
        firstName: string | null;
        lastName: string | null;
        avatarUrl: string | null;
    };
};

/** UI-shaped member row (legacy field aliases for existing Community screens). */
export type Member = {
    id: string;
    uuid: string;
    userId: string;
    membershipId: string;
    firstName: string;
    lastName: string;
    name: string;
    email: string;
    username: string;
    imgUrl: string;
    status: string;
    parishionerId: string;
    isDefault: boolean;
    joinedAt: string;
    additionalInfo: MemberAdditionalInfo | null;
    /** Raw API row for advanced consumers. */
    raw: ApiMember;
};

/**
 * `custom.parishionerId` wins whenever the key is PRESENT (any value, including `''`). Only a row
 * without the key in `custom` falls back to the legacy root-level `parishionerId`.
 *
 * The backend REMOVES a custom key sent as `null`, so after a `null` clear the key is absent. That is
 * why clearing an ID on a row that still carries a legacy root value sends `''` instead
 * (`parishionerIdClearValue`): the present-but-empty key keeps the old root value from reappearing.
 */
export function readParishionerId(info: MemberAdditionalInfo | null | undefined): string {
    if (!info || typeof info !== 'object') return '';
    const custom = info.custom && typeof info.custom === 'object' ? info.custom : undefined;
    const value = custom && 'parishionerId' in custom ? custom.parishionerId : info.parishionerId;
    return value == null ? '' : String(value);
}

/**
 * What to send as `custom.parishionerId` to CLEAR the ID. `null` removes the key server-side (and frees
 * its slot under the custom-key cap) — but if the row still has a legacy root-level `parishionerId`,
 * removing the custom key would make that old value show again, so send `''` to override it.
 */
export function parishionerIdClearValue(info: MemberAdditionalInfo | null | undefined): '' | null {
    return info && typeof info === 'object' && info.parishionerId != null && info.parishionerId !== '' ? '' : null;
}

export function normalizeMember(row: ApiMember): Member {
    const user = row.user || ({} as ApiMember['user']);
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    const name = [firstName, lastName].filter(Boolean).join(' ').trim() || user.email || '';
    const info = (
        row.additionalInfo && typeof row.additionalInfo === 'object' && !Array.isArray(row.additionalInfo) ? row.additionalInfo : null
    ) as MemberAdditionalInfo | null;

    return {
        id: user.id,
        uuid: user.id,
        userId: user.id,
        membershipId: row.id,
        firstName,
        lastName,
        name,
        email: user.email || '',
        username: user.email || '',
        imgUrl: user.avatarUrl || '',
        status: 'Member',
        parishionerId: readParishionerId(info),
        isDefault: Boolean(row.isDefault),
        joinedAt: row.joinedAt || '',
        additionalInfo: info,
        raw: row,
    };
}

/**
 * PATCH /parishes/:parishId/members/:userId body. `custom` is merged key-by-key into
 * `additionalInfo.custom`, so only the keys being changed need to be sent. `null` clears a key.
 */
export type UpdateMemberPayload = {
    custom: Record<string, unknown>;
};

export type MemberDonationStatus = 'SUCCEEDED' | 'PROCESSING' | 'PARTIALLY_REFUNDED' | 'REFUNDED' | 'DISPUTED';

/** One row of GET /v1/parishes/:parishId/members/:userId/donations (CLERK+). */
export type MemberDonation = {
    id: string;
    basketId: string;
    basketName: string;
    amountCents: number;
    refundedCents: number;
    /** Server-computed: amountCents − refundedCents, and 0 for a DISPUTED gift. Never recompute. */
    netCents: number;
    currency: string;
    status: MemberDonationStatus;
    createdAt: string;
};

/** Totals over EVERY gift matching the request's date range — not just the returned page. */
export type MemberDonationSummary = {
    count: number;
    /** Net given (refunded and disputed gifts count as 0). */
    netCents: number;
    lastDonationAt: string | null;
};

export type MemberDonationsPage = {
    items: MemberDonation[];
    total: number;
    page: number;
    pageSize: number;
    summary: MemberDonationSummary;
};

export type MemberDonationsQuery = {
    page: number;
    pageSize: number;
    /** Inclusive ISO-8601 instants (with offset); omit both for all time. */
    from?: string;
    to?: string;
};
